import {
  BooleanInput,
  DateInput,
  TabbedForm,
  required,
  useNotify,
  usePermissions,
  useRedirect,
  useRefresh,
  useTranslate,
} from "react-admin";
import {
  FormTab,
  Group,
  GroupItem,
  Toolbar,
  TransactionLogsField,
  TransactionNotesInput,
  TransactionNotesIsPrivateInput,
  ValidationSummary,
  useFieldLabel,
  useSaveMutation,
  useTabVisibility,
} from "ra-friendsofbabba";
import React, { useMemo } from "react";

import { ACCEPTED_FILE_TYPES } from "../config";
import ActivityInput from "./inputs/ActivityInput";
import ChatIcon from "@material-ui/icons/Chat";
import Input from "../component/input/Input";
import { MediaInput } from "../component/input";
import PrintAlert from "../component/alert/PrintAlert";
import { PrintButton } from "../component/button";
import PrintWrapperButton from "./buttons/PrintWrapperButton";
import RepliesField from "./fields/RepliesField";
import ResearchProjectInput from "./inputs/ResearchProjectInput";
import TextContentInput from "../component/input/TextContentInput";
import UserInput from "./inputs/UserInput";
import { get } from "lodash";
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  boolean: {
    marginTop: theme.spacing(3),
  },
}));

const FormToolbar = (props) => {
  const visible = useTabVisibility(1);
  if (visible) {
    return <br />;
  }
  return (
    <Toolbar {...props} useWorkflow maxButtonsToDisplay={1}>
      <PrintWrapperButton label="ra.action.generate_pdf" />
    </Toolbar>
  );
};

const Form = (props) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const translate = useTranslate();
  const save = useSaveMutation({
    ...props,
    onSuccess: (response, values) => {
      const prevState = get(values, "transaction.state");
      const nextState = get(response, "data.state");
      const isMoved = prevState !== nextState;
      notify(
        `resources.technical-reports.messages.${values.state}_${
          isMoved ? "moved" : "loop"
        }.save_success`
      );
      if (values.id > 0) {
        refresh();
      } else {
        redirect("edit", props.basePath, response.data.id);
      }
    },
  });
  const classes = useStyles();
  const fieldLabel = useFieldLabel({ ...props });
  const { loaded, permissions } = usePermissions();
  const { isAdmin, isTro, isResearcher, isDraft } = useMemo(() => {
    const isAdmin = loaded && permissions(["admin"]);
    const isTro = loaded && permissions(["tro"]);
    const isResearcher = loaded && permissions(["researcher"]);
    const isDraft =
      props?.record?.transaction?.state === "draft" ||
      props?.record?.transaction?.state === "integrations_requested";
    return { isAdmin, isTro, isResearcher, isDraft };
  }, [loaded, permissions, props]);
  return (
    <TabbedForm
      {...props}
      save={save}
      initialValues={{
        state: "draft",
        is_private: false,
        has_public_activity: false,
        has_private_activity: false,
        has_foreign_activity: false,
      }}
      toolbar={<FormToolbar />}
    >
      <FormTab
        label={translate("resources.technical-reports.tabs.general", {
          ...props?.record,
          smart_count: props?.record?.id > 0 ? 1 : 0,
        })}
      >
        {!isTro && <ValidationSummary />}
        <Input
          source="user_id"
          fullWidth
          validate={required()}
          component={<UserInput isResearcher={isResearcher} />}
        />
        <Input
          source="research_project_id"
          fullWidth
          validate={required()}
          component={<ResearchProjectInput />}
        />
        <Group fullWidth>
          <GroupItem lg={4} md={5} sm={6} xs={12}>
            <Input
              source="from_date"
              validate={required()}
              component={<DateInput />}
            />
          </GroupItem>
          <GroupItem lg={4} md={5} sm={6} xs={12}>
            <Input
              source="to_date"
              validate={required()}
              component={<DateInput />}
            />
          </GroupItem>
        </Group>

        <Input
          source="has_public_activity"
          validate={required()}
          className={classes.boolean}
          component={<BooleanInput />}
          fullWidth
        />

        <Input
          source="public_data"
          type="public"
          fullWidth
          component={<ActivityInput />}
        />

        <Input
          source="has_private_activity"
          validate={required()}
          className={classes.boolean}
          component={<BooleanInput />}
          fullWidth
        />
        <Input
          source="private_data"
          type="private"
          fullWidth
          component={<ActivityInput />}
        />

        <Input
          source="has_foreign_activity"
          validate={required()}
          className={classes.boolean}
          component={<BooleanInput />}
          fullWidth
        />
        <Input
          source="foreign_data"
          type="foreign"
          fullWidth
          component={<ActivityInput />}
        />

        <TextContentInput
          source="deliverables_achieved"
          fullWidth
          multiline
          maxLength={1500}
        />
        <Input
          source="deliverables_media"
          acceptedFormats={ACCEPTED_FILE_TYPES}
          component={<MediaInput />}
          fullWidth
        />
        <TextContentInput
          source="problem_and_solution"
          fullWidth
          multiline
          maxLength={1500}
        />
        {isDraft && (
          <PrintAlert>
            <PrintButton label="ra.action.generate_pdf" />
          </PrintAlert>
        )}
        <Input source="media" component={<MediaInput />} fullWidth />

        <TransactionLogsField source="logs" admin={isAdmin} fullWidth />
        <TransactionNotesInput
          source="notes"
          helperText={fieldLabel("notes.help")}
          fullWidth
        />
        <TransactionNotesIsPrivateInput
          admin={isAdmin}
          source="is_private"
          fullWidth
        />
      </FormTab>
      <FormTab
        icon={<ChatIcon />}
        label="resources.technical_reports.tabs.replies"
      >
        <RepliesField source="replies" />
      </FormTab>
    </TabbedForm>
  );
};
export default Form;
